import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useStateMachine } from 'little-state-machine'
import { updateStore } from '../../store'
import { useAuthStatus, useToken } from '../../utils'
import { usePreContractList, useCancelPreContract } from '../../hooks/offer'
import { formatDateToYYYYMMDD } from '../../utils'
import { useUser } from '../../hooks'
import { useCaddieProfileModal, useProProfileModal } from '../../contexts/ModalContext'
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid'
import styles from './PreContractManagementDefaultView.module.scss'

/**
 * 체결된 사전계약 목록 + 정식계약 전환하기 버튼이 있는 공통 섹션
 * @param {Object} props
 * @param {'home'|'management'} props.variant - home: 정식계약 전환하기 버튼 활성화 후 제안 플로우 이동, management: 정식계약 제안 비활성
 * @param {boolean} props.showTitle - "체결된 사전계약" 타이틀 표시 여부
 */
export const AcceptedPreContractsSection = ({ variant = 'management', showTitle = true }) => {
	const [auth] = useAuthStatus()
	const token = useToken()
	const navigate = useNavigate()
	const { actions, state } = useStateMachine({ updateStore })
	const { data: userInfo } = useUser()
	const cancelPreContractMutation = useCancelPreContract()
	const caddieProfileModal = useCaddieProfileModal()
	const proProfileModal = useProProfileModal()
	const { data: preContractData } = usePreContractList(token)

	const getAcceptedPreContracts = () => {
		if (!preContractData?.data || !auth?.user?.id) return []
		return preContractData.data.filter((item) => item?.pre_contract_status === 'ACCEPTED')
	}

	const handleCancelPreContract = (preContractId) => {
		if (cancelPreContractMutation.isPending) return
		const confirmCancel = window.confirm('사전계약을 취소하시겠습니까?')
		if (!confirmCancel) return
		cancelPreContractMutation.mutate(
			{ token, preContractId },
			{
				onSuccess: () => alert('사전계약이 취소되었습니다.'),
				onError: () => alert('사전계약 취소에 실패했습니다.'),
			},
		)
	}

	const handleShowProfile = (user) => {
		if (user?.user_type === 'tourPro') {
			proProfileModal.show({ user, onClose: () => {} })
		} else {
			caddieProfileModal.show({ user, onClose: () => {} })
		}
	}

	const handleFormalContract = (item) => {
		const targetUser = item.target_user_id === auth?.user?.id ? item.user : item.targetUser
		const isPro = auth?.user?.userType === 'tourPro'
		actions.updateStore({
			offerInput: {
				...state.offerInput,
				targetUserId: targetUser?.id,
				...(isPro ? { selectedCaddie: targetUser } : { selectedPro: targetUser }),
			},
		})
		navigate('/search?tab=partner', {
			state: { fromPreContractId: item.id, targetUserId: targetUser?.id },
		})
	}

	const acceptedList = getAcceptedPreContracts()

	const renderItem = (item) => {
		const displayUser = item.target_user_id === auth?.user?.id ? item.user : item.targetUser
		const isHome = variant === 'home'

		return (
			<div key={item.id} className={styles.acceptedContractItem}>
				<div className={styles.acceptedContractContent}>
					<div className={styles.acceptedContractInfo}>
						<div className={styles.acceptedContractDate}>
							계약일: {formatDateToYYYYMMDD(item.created_at)}
						</div>
						<div
							className={styles.acceptedContractUser}
							onClick={() => handleShowProfile(displayUser)}
							style={{ cursor: 'pointer' }}
							role="button"
							tabIndex={0}
							onKeyDown={(e) => e.key === 'Enter' && handleShowProfile(displayUser)}>
							<div
								className={styles.userType}
								style={{
									fontSize: '12px',
									fontWeight: '500',
									color: 'white',
									backgroundColor: 'black',
									padding: '0px 8px',
									borderRadius: '4px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}>
								{displayUser?.user_type === 'tourPro' ? '투어프로' : '투어캐디'}
							</div>
							<div className={styles.userName}>
								{displayUser?.name || '사용자'}
								{displayUser?.user_type === 'tourPro' ? ' 프로' : ' 캐디'}
							</div>
						</div>
						{item?.message && (
							<div
								style={{
									marginTop: '8px',
									fontSize: '12px',
									color: 'black',
									fontStyle: 'italic',
									margin: '0px 0px 8px 0px',
									whiteSpace: 'pre-wrap',
									wordWrap: 'break-word',
								}}>
								"{item.message}"
							</div>
						)}
					</div>
					<div className={styles.acceptedContractActions}>
						{isHome ? (
							<button
								type="button"
								className={styles.formalContractButtonActive}
								onClick={() => handleFormalContract(item)}>
								정식계약 전환하기
							</button>
						) : (
							<button type="button" className={styles.formalContractButton} disabled>
								정식계약 제안
							</button>
						)}
					</div>
				</div>
			</div>
		)
	}

	// 체결된 사전계약이 없을 때
	if (acceptedList.length === 0) {
		// 홈에서는 체결된 사전계약이 없으면 섹션 자체를 숨김
		if (variant === 'home') return null

		const searchLink = '/search'
		return (
			<div className={styles.acceptedContractsSection}>
				{showTitle && (
					<div className={styles.acceptedContractsTitleRow}>
						<div className={styles.acceptedContractsTitle}>체결된 사전계약</div>
					</div>
				)}
				<div className={styles.acceptedEmptyState}>
					<div className={styles.acceptedEmptyTitle}>체결된 사전계약이 없습니다</div>
					<div className={styles.acceptedEmptyDescription}>
						{userInfo?.user_type === 'tourPro'
							? '캐디를 찾아서 사전계약을 시작해보세요!'
							: '프로를 찾아서 사전계약을 시작해보세요!'}
					</div>
					<button
						type="button"
						className={styles.acceptedEmptyButton}
						onClick={() => navigate(searchLink)}>
						<MagnifyingGlassIcon
							style={{ width: '16px', height: '16px', color: 'white' }}
						/>
						{userInfo?.user_type === 'tourPro' ? '캐디 찾기' : '프로 찾기'}
					</button>
				</div>
			</div>
		)
	}

	return (
		<div className={styles.acceptedContractsSection}>
			{showTitle && (
				<div className={styles.acceptedContractsTitleRow}>
					<div className={styles.acceptedContractsTitle}>체결된 사전계약</div>
				</div>
			)}
			<div className={styles.acceptedContractsList}>{acceptedList.map(renderItem)}</div>
		</div>
	)
}
